import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Crop, RotateCw, FlipHorizontal2, FlipVertical2, Save, RotateCcw } from "lucide-react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { toast } from "sonner";

import { BACKEND } from "../../lib/backend";

/**
 * Image Studio modal. Applies transforms in this order on the backend:
 *   crop -> flip H -> flip V -> rotate (bg-filled) -> brightness -> contrast
 * Preview shows the same order via CSS transforms (crop emulated by overflow).
 *
 * Props:
 *   imageUrl   — current /api/files/... URL of the slide image
 *   onSaved(newUrl) — called with the new image URL
 */
export default function ImageEditorDialog({ open, onOpenChange, imageUrl, onSaved }) {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState(undefined);
  const [completedCrop, setCompletedCrop] = useState(undefined);
  const [rotate, setRotate] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [brightness, setBrightness] = useState(1);
  const [contrast, setContrast] = useState(1);
  const [bgColor, setBgColor] = useState("#FFFFFF");
  const [aspect, setAspect] = useState(undefined);
  const [saving, setSaving] = useState(false);

  // Reset state when the dialog opens with a new image
  useEffect(() => {
    if (open) {
      setCrop(undefined);
      setCompletedCrop(undefined);
      setRotate(0);
      setFlipH(false);
      setFlipV(false);
      setBrightness(1);
      setContrast(1);
      setBgColor("#FFFFFF");
      setAspect(undefined);
    }
  }, [open, imageUrl]);

  const onImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    // start with the full image selected
    const initial = centerCrop(
      makeAspectCrop({ unit: "%", width: 100 }, naturalWidth / naturalHeight, naturalWidth, naturalHeight),
      naturalWidth,
      naturalHeight,
    );
    setCrop(initial);
    setCompletedCrop({ ...initial, unit: "%" });
  };

  const setAspectRatio = (ratio) => {
    setAspect(ratio);
    if (imgRef.current) {
      const { naturalWidth, naturalHeight } = imgRef.current;
      const next = centerCrop(
        makeAspectCrop({ unit: "%", width: 80 }, ratio || naturalWidth / naturalHeight, naturalWidth, naturalHeight),
        naturalWidth,
        naturalHeight,
      );
      setCrop(next);
      setCompletedCrop({ ...next, unit: "%" });
    }
  };

  const resetAll = () => {
    setRotate(0);
    setFlipH(false);
    setFlipV(false);
    setBrightness(1);
    setContrast(1);
    setBgColor("#FFFFFF");
    setAspect(undefined);
    if (imgRef.current) {
      const { naturalWidth, naturalHeight } = imgRef.current;
      const initial = centerCrop(
        makeAspectCrop({ unit: "%", width: 100 }, naturalWidth / naturalHeight, naturalWidth, naturalHeight),
        naturalWidth,
        naturalHeight,
      );
      setCrop(initial);
      setCompletedCrop({ ...initial, unit: "%" });
    }
  };

  const apply = async () => {
    if (!imageUrl) return;
    // Convert crop to normalised 0..1 in source coords
    let cropBox = null;
    if (completedCrop && imgRef.current) {
      if (completedCrop.unit === "%") {
        cropBox = {
          x: (completedCrop.x || 0) / 100,
          y: (completedCrop.y || 0) / 100,
          w: (completedCrop.width || 100) / 100,
          h: (completedCrop.height || 100) / 100,
        };
      } else {
        const { naturalWidth, naturalHeight } = imgRef.current;
        cropBox = {
          x: (completedCrop.x || 0) / naturalWidth,
          y: (completedCrop.y || 0) / naturalHeight,
          w: (completedCrop.width || naturalWidth) / naturalWidth,
          h: (completedCrop.height || naturalHeight) / naturalHeight,
        };
      }
      // clamp
      cropBox.x = Math.max(0, Math.min(1, cropBox.x));
      cropBox.y = Math.max(0, Math.min(1, cropBox.y));
      cropBox.w = Math.max(0.01, Math.min(1 - cropBox.x, cropBox.w));
      cropBox.h = Math.max(0.01, Math.min(1 - cropBox.y, cropBox.h));
    }

    setSaving(true);
    try {
      const { data } = await api.post("/uploads/image/edit", {
        image_url: imageUrl,
        crop: cropBox,
        rotate: rotate,
        flip_h: flipH,
        flip_v: flipV,
        brightness,
        contrast,
        bg_color: bgColor,
      });
      toast.success("Image updated");
      onSaved?.(data.url);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Edit failed");
    } finally {
      setSaving(false);
    }
  };

  if (!imageUrl) return null;
  const src = imageUrl.startsWith("http") ? imageUrl : `${BACKEND}${imageUrl}`;

  // Preview transform (post-crop): scale(flip) rotate filter brightness/contrast
  const previewTransform = `scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1}) rotate(${rotate}deg)`;
  const previewFilter = `brightness(${brightness}) contrast(${contrast})`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-5xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Crop className="w-4 h-4 text-blue-400" /> Image studio
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Crop, rotate, flip, and tune brightness/contrast. Backend pipeline: crop → flip → rotate → brightness → contrast.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-12 gap-4">
          {/* Cropper */}
          <div className="col-span-7 bg-zinc-950 border border-zinc-800 rounded-md p-2 grid place-items-center min-h-[420px]">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c, pc) => setCompletedCrop(pc)}
              aspect={aspect}
              keepSelection
              className="max-h-[60vh]"
            >
              <img
                ref={imgRef}
                alt="Editing"
                src={src}
                onLoad={onImageLoad}
                style={{ maxHeight: "60vh", display: "block" }}
                data-testid="image-editor-source"
              />
            </ReactCrop>
          </div>

          {/* Controls + preview */}
          <div className="col-span-5 space-y-3">
            <div>
              <Label className="label-eyebrow">Aspect ratio</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {[
                  { l: "Free", v: undefined },
                  { l: "1:1", v: 1 },
                  { l: "16:9", v: 16 / 9 },
                  { l: "4:5", v: 4 / 5 },
                  { l: "3:2", v: 3 / 2 },
                ].map((opt) => (
                  <Button
                    key={opt.l}
                    type="button"
                    size="sm"
                    data-testid={`crop-aspect-${opt.l.toLowerCase().replace(":", "x")}`}
                    variant={aspect === opt.v ? "default" : "outline"}
                    onClick={() => setAspectRatio(opt.v)}
                    className={
                      aspect === opt.v
                        ? "bg-blue-600 hover:bg-blue-500 h-7 text-xs"
                        : "border-zinc-800 hover:bg-zinc-800 text-zinc-300 h-7 text-xs"
                    }
                  >
                    {opt.l}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label className="label-eyebrow">Rotation · {rotate.toFixed(0)}°</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  data-testid="rotate-slider"
                  type="range"
                  min={-180}
                  max={180}
                  step={1}
                  value={rotate}
                  onChange={(e) => setRotate(Number(e.target.value))}
                  className="flex-1 accent-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setRotate((r) => Math.round((r - 90) / 90) * 90 || 0)}
                  className="text-zinc-400 hover:text-zinc-100 p-1 rounded hover:bg-zinc-800"
                  data-testid="rotate-90-ccw"
                  title="Rotate −90°"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setRotate((r) => Math.round((r + 90) / 90) * 90 || 0)}
                  className="text-zinc-400 hover:text-zinc-100 p-1 rounded hover:bg-zinc-800"
                  data-testid="rotate-90-cw"
                  title="Rotate +90°"
                >
                  <RotateCw className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                data-testid="flip-h"
                variant={flipH ? "default" : "outline"}
                onClick={() => setFlipH((v) => !v)}
                className={
                  flipH
                    ? "bg-blue-600 hover:bg-blue-500 h-7 text-xs"
                    : "border-zinc-800 hover:bg-zinc-800 text-zinc-300 h-7 text-xs"
                }
              >
                <FlipHorizontal2 className="w-3 h-3 mr-1" /> Flip H
              </Button>
              <Button
                type="button"
                size="sm"
                data-testid="flip-v"
                variant={flipV ? "default" : "outline"}
                onClick={() => setFlipV((v) => !v)}
                className={
                  flipV
                    ? "bg-blue-600 hover:bg-blue-500 h-7 text-xs"
                    : "border-zinc-800 hover:bg-zinc-800 text-zinc-300 h-7 text-xs"
                }
              >
                <FlipVertical2 className="w-3 h-3 mr-1" /> Flip V
              </Button>
              <Button
                type="button"
                size="sm"
                data-testid="reset-all"
                variant="ghost"
                onClick={resetAll}
                className="text-zinc-400 hover:text-zinc-100 h-7 text-xs ml-auto"
              >
                Reset
              </Button>
            </div>

            <div>
              <Label className="label-eyebrow">Brightness · {brightness.toFixed(2)}</Label>
              <input
                data-testid="brightness-slider"
                type="range" min={0.3} max={1.7} step={0.01}
                value={brightness} onChange={(e) => setBrightness(Number(e.target.value))}
                className="w-full accent-blue-500"
              />
            </div>

            <div>
              <Label className="label-eyebrow">Contrast · {contrast.toFixed(2)}</Label>
              <input
                data-testid="contrast-slider"
                type="range" min={0.3} max={1.7} step={0.01}
                value={contrast} onChange={(e) => setContrast(Number(e.target.value))}
                className="w-full accent-blue-500"
              />
            </div>

            <div>
              <Label className="label-eyebrow">Background fill (rotated corners)</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  data-testid="bg-color-input"
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="w-8 h-8 bg-transparent border border-zinc-800 rounded cursor-pointer"
                />
                <span className="text-xs font-mono text-zinc-400">{bgColor}</span>
              </div>
            </div>

            <div className="border border-zinc-800 rounded-md p-2 bg-zinc-950">
              <div className="label-eyebrow mb-1">Preview</div>
              <div
                className="h-32 grid place-items-center overflow-hidden rounded-sm"
                style={{ background: bgColor }}
              >
                <img
                  data-testid="image-editor-preview"
                  src={src}
                  alt="preview"
                  className="max-h-full max-w-full object-contain"
                  style={{ transform: previewTransform, filter: previewFilter, transition: "transform 120ms, filter 120ms" }}
                />
              </div>
              <p className="text-[10px] text-zinc-500 mt-1">
                Crop isn’t applied to this thumbnail — adjust the crop in the canvas on the left.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            data-testid="image-editor-apply"
            onClick={apply}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-500"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : "Apply & save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
